import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, Skeleton } from '@mui/material'

import { useIntersectionObserverEntry } from '../../../../hooks/useIntersectionObserverEntry'
import { borderRadiusDefault } from '../../../../themes/theme'
import { BlueprintHierarchyTrail, HierarchyTrailMenu, HierarchyTrailSegment } from '../BlueprintHierarchyTrail/BlueprintHierarchyTrail'

// Matches `$app-header-height` (95px) — same sticky offset used by
// `GRTabNavigation` / `MarketExplorerPage` / `DataGlossaryPage`'s sticky bars.
export const HIERARCHY_BAR_STICKY_TOP = 95
// Fixed bar height. Crumb texts are vertically centered inside it, while the
// chevron separators stretch to this full height so they cut across the whole
// bar edge to edge. Exported so pages can offset anything else that's sticky
// (e.g. the sibling rail) below this bar instead of colliding with it.
export const HIERARCHY_BAR_STICKY_HEIGHT = 48

const CATALOGUE_PATH = '/live-event-analytics'

// Sentinel sits right above the card at its natural (non-stuck) position;
// shrinking the observed viewport by the sticky offset means the sentinel
// stops intersecting the instant the card starts sticking to `top`.
const sentinelObserverOptions = { rootMargin: `-${HIERARCHY_BAR_STICKY_TOP}px 0px 0px 0px`, threshold: 0 }

// Sibling switchers per level, keyed by the level they hang off. Each page
// builds these itself because "switch to this sibling" means different things
// per page — a filter change on the catalogue, a navigation on a detail page.
export interface BlueprintHierarchyMenus {
  eventCategory?: HierarchyTrailMenu
  eventType?: HierarchyTrailMenu
  blueprint?: HierarchyTrailMenu
}

// Router targets for the levels that have a page of their own, so a resolved
// crumb can be navigated into (and not just switched sideways). Pass only the
// levels that lead somewhere else than the current page — the level being viewed
// must not link to itself.
export interface BlueprintHierarchyLinks {
  eventCategory?: string
  eventType?: string
  blueprint?: string
}

interface BlueprintHierarchyBarProps {
  // Translated event-category (tag group) name, e.g. "Monetization".
  categoryName?: string
  // Translated event-type (tag) name, e.g. "Bundle/Offer".
  eventTypeName?: string
  // Translated blueprint name, e.g. "Basic Bundle".
  blueprintName?: string
  // Query string (including leading "?") carried back to the catalogue on the
  // root crumb so the user's segment filters and window survive the round trip.
  catalogueSearch?: string
  // Set on the catalogue itself: the root crumb stops being a link, since we are
  // already there.
  isCatalogueRoot?: boolean
  // Sibling dropdowns for the resolved levels.
  menus?: BlueprintHierarchyMenus
  // Navigation targets for the resolved levels that have their own page.
  links?: BlueprintHierarchyLinks
  // Router state carried on the `links` above (the catalogue view the target
  // page's root crumb should return to).
  linkState?: unknown
  isLoading?: boolean
}

/**
 * The persistent hierarchy bar shown on every level of the tool: the
 * `Overview › {category} › {event type} › {blueprint}` trail in a
 * sticky bar. Levels the page can't name render as grayed-out placeholders, so
 * the trail is always the same four crumbs deep and fills in as the selection
 * narrows; on detail pages the root crumb doubles as "back to catalog". Levels
 * given a `menu` become sibling switchers, so the bar is also the fastest way to
 * move sideways in the hierarchy; levels given a `link` also navigate into their
 * own page (label navigates, chevron still switches).
 *
 * Sticks below the app header while scrolling, matching the sticky toolbar
 * pattern used elsewhere (`GRTabNavigation`, Market Explorer / Data Glossary
 * sub-headers) — and, like `GRTabNavigation`, flattens its top corners while
 * pinned so it reads as flush against the header edge.
 */
export const BlueprintHierarchyBar: React.FC<BlueprintHierarchyBarProps> = ({
  categoryName,
  eventTypeName,
  blueprintName,
  catalogueSearch = '',
  isCatalogueRoot,
  menus,
  links,
  linkState,
  isLoading,
}) => {
  const { t } = useTranslation()

  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isPinned, setIsPinned] = useState(false)
  // Stable, or the hook would create and observe a new IntersectionObserver every render.
  const handleSentinelEntry = useCallback((entry: IntersectionObserverEntry) => setIsPinned(!entry.isIntersecting), [])
  useIntersectionObserverEntry(sentinelRef, sentinelObserverOptions, handleSentinelEntry)

  const segments = useMemo<HierarchyTrailSegment[]>(() => {
    // Levels below the root, in hierarchy order, each with the label its
    // placeholder falls back to while the level is unresolved.
    const levels = [
      { key: 'eventCategory', name: categoryName, placeholder: t('market-analytics:event_category'), menu: menus?.eventCategory, to: links?.eventCategory },
      { key: 'eventType', name: eventTypeName, placeholder: t('market-analytics:event_type'), menu: menus?.eventType, to: links?.eventType },
      { key: 'blueprint', name: blueprintName, placeholder: t('market-analytics:granularity_blueprint'), menu: menus?.blueprint, to: links?.blueprint },
    ]
    const deepestResolved = levels.reduce((deepest, level, index) => (level.name ? index : deepest), -1)

    const root: HierarchyTrailSegment = isCatalogueRoot
      ? { key: 'root', label: t('market-analytics:hierarchy_root'), isCurrent: deepestResolved === -1 }
      : { key: 'root', label: t('market-analytics:hierarchy_root'), to: `${CATALOGUE_PATH}${catalogueSearch}` }

    const resolved = levels.flatMap<HierarchyTrailSegment>((level, index) => {
      if (level.name)
        return [
          { key: level.key, label: level.name, isCurrent: index === deepestResolved, menu: level.menu, to: level.to, state: level.to ? linkState : undefined },
        ]
      // Unnamed levels stay as placeholders so the trail reads as the full depth
      // of the hierarchy everywhere, not just on the catalogue: dropping them
      // made the bar lose its blueprint level the moment you opened an event
      // type page, as if the hierarchy had gotten shallower on the way in.
      // A placeholder still gets its menu when one is supplied — picking a level
      // straight from the empty crumb is the fastest way down.
      return [{ key: level.key, label: level.placeholder, isPlaceholder: true, menu: level.menu }]
    })

    return [root, ...resolved]
  }, [categoryName, eventTypeName, blueprintName, catalogueSearch, isCatalogueRoot, menus, links, linkState, t])

  return (
    <>
      <Box ref={sentinelRef} sx={{ height: 0 }} />
      <Card
        sx={{
          display: 'flex',
          alignItems: 'center',
          // Segment backgrounds run edge to edge; the card's rounded corners
          // clip them (`overflow: hidden`), so no horizontal padding here —
          // the crumb list items carry their own.
          px: 0,
          py: 0,
          mb: 2,
          overflow: 'hidden',
          height: HIERARCHY_BAR_STICKY_HEIGHT,
          position: 'sticky',
          top: HIERARCHY_BAR_STICKY_TOP,
          zIndex: 10,
          borderTopLeftRadius: isPinned ? 0 : borderRadiusDefault,
          borderTopRightRadius: isPinned ? 0 : borderRadiusDefault,
          transition: 'border-radius 0.15s ease-in-out',
        }}
      >
        {isLoading ? (
          <Skeleton variant="text" width={320} sx={{ mx: 2 }} />
        ) : (
          <BlueprintHierarchyTrail segments={segments} ariaLabel={t('market-analytics:hierarchy_aria')} />
        )}
      </Card>
    </>
  )
}
